import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Box,
  Typography,
  Grid,
  Stack,
  Alert,
  Paper,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  IconButton,
  Chip,
  Divider
} from '@mui/material';
import {
  Close as CloseIcon,
  Visibility as VisibilityIcon,
  Edit as EditIcon,
  Schedule as ScheduleIcon,
  Add as AddIcon,
  Person as PersonIcon,
  Print as PrintIcon,
  Warning as WarningIcon,
  Delete as DeleteIcon,
  DeleteSweep as DeleteSweepIcon
} from '@mui/icons-material';
import { format } from 'date-fns';
import { formatDate } from '../../utils/dateUtils';
import { formatTime12h, esCitaGrupal } from '../../utils/calendarHelpers';
import { esFeriadoPeruano, obtenerNombreFeriado } from '../../utils/feriadosPeruanos';
import WhatsAppReminderButton from './WhatsAppReminderButton';

const EventDialog = ({
  open,
  onClose,
  selectedEvent,
  onViewCita,
  onEditCita,
  getUserRole,
  
  // Estados para reprogramación
  reprogramandoCita,
  nuevaFecha,
  setNuevaFecha,
  nuevaHoraInicio,
  setNuevaHoraInicio,
  nuevaHoraFin,
  setNuevaHoraFin,
  onIniciarReprogramacion,
  onConfirmarReprogramacion,
  onCancelarReprogramacion,
  
  // Estados para próxima sesión
  programandoProximaSesion,
  proximaFecha,
  setProximaFecha,
  proximaHoraInicio,
  setProximaHoraInicio,
  proximaHoraFin,
  setProximaHoraFin,
  onIniciarProximaSesion,
  onConfirmarProximaSesion,
  onCancelarProximaSesion,
  
  // Estados para modificar horario
  modificandoHorario,
  nuevoPatronDia,
  setNuevoPatronDia,
  nuevoPatronHoraInicio,
  setNuevoPatronHoraInicio,
  nuevoPatronHoraFin,
  setNuevoPatronHoraFin,
  citasAfectadas,
  onIniciarModificarHorario,
  onConfirmarModificarHorario,
  onCancelarModificarHorario,
  
  // Estados para imprimir horario
  imprimiendoHorario,
  horarioParaImprimir,
  onIniciarImprimirHorario,
  onGenerarImpresion,
  onCancelarImpresion,
  
  // Funciones de cambio de estado
  onEstadoChange,
  
  // ✅ NUEVAS PROPS para eliminación
  onEliminarCita,
  onEliminarCitaYPosteriores,
  
  // Contexto de notificaciones
  showNotification
}) => {
  if (!selectedEvent) return null;

  // ✅ NUEVAS FUNCIONES: Manejar eliminación
  const handleEliminarCita = () => {
    if (!window.confirm('¿Está seguro de que desea eliminar permanentemente esta cita? Esta acción no se puede deshacer.')) {
      return;
    }
    onEliminarCita(selectedEvent.id);
    onClose();
  };

  const handleEliminarCitaYPosteriores = () => {
    onEliminarCitaYPosteriores(selectedEvent.id);
    onClose();
  };

  // Verificar si puede editar/eliminar
  const canEditDelete = () => {
    const userRole = getUserRole();
    return ['Administrador', 'Admisión'].includes(userRole);
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          '@media (max-width:600px)': {
            margin: 0,
            height: '100%',
            maxHeight: '100%',
            borderRadius: 0,
          },
        },
      }}
    >
      <DialogTitle sx={{ pb: 1 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h6">
            {selectedEvent.es_evento_personal ? 'Evento Personal' : 
             esCitaGrupal(selectedEvent) ? 'Cita Grupal' : 'Detalle de Cita'}
          </Typography>
          
          {/* Botones de acción rápida */}
          <Box display="flex" gap={1}>
            {!selectedEvent.es_evento_personal && (
              <>
                <IconButton 
                  size="small" 
                  color="primary"
                  onClick={onViewCita}
                  title="Ver detalles completos"
                >
                  <VisibilityIcon />
                </IconButton>
                
                {/* SOLO MOSTRAR EDITAR Y WHATSAPP SI TIENE PERMISOS */}
                {canEditDelete() && (
                  <>
                    <IconButton 
                      size="small" 
                      color="warning"
                      onClick={onEditCita}
                      title="Editar cita"
                    >
                      <EditIcon />
                    </IconButton>
                    
                    {selectedEvent.paciente?.whatsapp && (
                      <WhatsAppReminderButton 
                        citaId={selectedEvent.id}
                        paciente={selectedEvent.paciente}
                        size="small"
                      />
                    )}
                  </>
                )}
              </>
            )}
            
            <IconButton size="small" onClick={onClose}>
              <CloseIcon />
            </IconButton>
          </Box>
        </Box>
      </DialogTitle>
      
      <DialogContent>
        <Grid container spacing={3}>
          {/* Columna izquierda - Información básica */}
          <Grid item xs={12} md={8}>
            <Stack spacing={2}>
              {/* 🔥 FUNCIONALIDAD RESTAURADA: Alert especial para citas reprogramadas */}
              {((selectedEvent.estado === 'Reprogramo' && selectedEvent.notas) || 
                (selectedEvent.notas && selectedEvent.notas.includes('Reprogramación de cita'))) && (
                <Alert 
                  severity="warning" 
                  icon={<WarningIcon />}
                  sx={{ 
                    backgroundColor: '#fff3e0',
                    border: '1px solid #ff9800',
                    '& .MuiAlert-icon': { color: '#ff9800' }
                  }}
                >
                  <Typography variant="subtitle2" sx={{ color: '#e65100', fontWeight: 'bold' }}>
                    Cita Reprogramada
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#bf360c', mt: 0.5 }}>
                    {selectedEvent.notas}
                  </Typography>
                </Alert>
              )}

              {/* Información de cita grupal */}
              {esCitaGrupal(selectedEvent) && (
                <Alert severity="info" icon={<PersonIcon />}>
                  <Typography variant="subtitle2" gutterBottom>
                    Cita Grupal - {selectedEvent.extendedProps?.totalPacientes || 1} pacientes
                  </Typography>
                  {selectedEvent.pacientesGrupo?.map((paciente, index) => (
                    <Typography key={index} variant="body2">
                      • {paciente}
                    </Typography>
                  ))}
                </Alert>
              )}

              {/* Fecha y hora */}
              <Box>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  📅 Fecha y Hora
                </Typography>
                <Typography variant="body1" fontWeight="medium">
                  {formatDate(selectedEvent.fecha, 'PPP')}
                </Typography>
                <Typography variant="body1">
                  {formatTime12h(selectedEvent.horaInicio)} - {formatTime12h(selectedEvent.horaFin)}
                </Typography>
              </Box>

              {/* Paciente */}
              {!selectedEvent.es_evento_personal && (
                <Box>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    👤 Paciente
                  </Typography>
                  <Typography variant="body1">
                    {selectedEvent.pacienteNombre}
                  </Typography>
                </Box>
              )}

              {/* Especialista */}
              <Box>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  👨‍⚕️ Especialista
                </Typography>
                <Typography variant="body1">
                  {selectedEvent.especialistaNombre}
                </Typography>
              </Box>

              {/* Especialidad y etapa */}
              {selectedEvent.especialidadNombre && (
                <Box>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    🏥 Especialidad
                  </Typography>
                  <Typography variant="body1">
                    {selectedEvent.especialidadNombre}
                  </Typography>
                </Box>
              )}

              {selectedEvent.etapaNombre && (
                <Box>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    📋 Tratamiento
                  </Typography>
                  <Typography variant="body1">
                    {selectedEvent.etapaNombre}
                    {selectedEvent.numeroSesion && ` - Sesión #${selectedEvent.numeroSesion}`}
                  </Typography>
                </Box>
              )}

              {/* Motivo */}
              {selectedEvent.motivo && (
                <Box>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    📝 {selectedEvent.es_evento_personal ? 'Descripción' : 'Motivo'}
                  </Typography>
                  <Typography variant="body2">
                    {selectedEvent.motivo}
                  </Typography>
                </Box>
              )}

              {/* 🔥 FUNCIONALIDAD RESTAURADA: Mostrar notas adicionales si existen y no son de reprogramación */}
              {selectedEvent.notas && 
               !selectedEvent.notas.includes('Reprogramación de cita') && 
               selectedEvent.estado !== 'Reprogramo' && (
                <Box>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    💬 Notas Adicionales
                  </Typography>
                  <Paper 
                    elevation={1} 
                    sx={{ 
                      p: 2, 
                      backgroundColor: '#f5f5f5',
                      border: '1px solid #e0e0e0'
                    }}
                  >
                    <Typography variant="body2" sx={{ fontStyle: 'italic' }}>
                      {selectedEvent.notas}
                    </Typography>
                  </Paper>
                </Box>
              )}
            </Stack>
          </Grid>

          {/* Columna derecha - Acciones rápidas */}
          <Grid item xs={12} md={4}>
            {/* VERIFICACIÓN DE PERMISOS */}
            {canEditDelete() ? (
              <Paper elevation={1} sx={{ p: 2, backgroundColor: 'grey.50' }}>
                <Typography variant="subtitle2" gutterBottom>
                  Acciones Rápidas
                </Typography>
                
                {/* Estado actual */}
                <Box mb={2}>
                  <Typography variant="caption" color="text.secondary">
                    Estado actual:
                  </Typography>
                  <Box mt={0.5}>
                    <Chip
                      label={selectedEvent.estado === 'Programada' ? 'Confirmada' : 
                             selectedEvent.estado === 'Cancelada' ? 'Justificada' : 
                             selectedEvent.estado}
                      sx={{ 
                        bgcolor: selectedEvent.color,
                        color: 'white',
                        fontWeight: 'medium'
                      }}
                    />
                  </Box>
                </Box>

                {/* Cambio rápido de estado - Solo si no está reprogramando */}
                {!reprogramandoCita && (
                  <FormControl fullWidth size="small" sx={{ mb: 2 }}>
                    <InputLabel>Cambiar Estado</InputLabel>
                    <Select
                      value={selectedEvent.estado}
                      label="Cambiar Estado"
                      onChange={(e) => onEstadoChange(selectedEvent.id, e.target.value)}
                    >
                      <MenuItem value="Realizada">✅ Realizada</MenuItem>
                      <MenuItem value="Justificada">❌ Justificada</MenuItem>
                      <MenuItem value="No asistió">⚠️ No asistió</MenuItem>
                    </Select>
                  </FormControl>
                )}

                {/* Formularios de reprogramación o próxima sesión */}
                {reprogramandoCita ? (
                  <Box>
                    <Typography variant="subtitle2" gutterBottom color="primary">
                      Reprogramar Cita
                    </Typography>
                    <Stack spacing={2}>
                      <TextField
                        label="Nueva Fecha"
                        type="date"
                        value={nuevaFecha}
                        onChange={(e) => {
                          const fechaSeleccionada = e.target.value;
                          if (esFeriadoPeruano(fechaSeleccionada)) {
                            showNotification(`${obtenerNombreFeriado(fechaSeleccionada)} es feriado nacional. No se puede programar citas en días festivos.`, 'error');
                            return; // ✅ No permitir seleccionar feriados
                          }
                          setNuevaFecha(fechaSeleccionada);
                        }}
                        inputProps={{
                          min: format(new Date(), 'yyyy-MM-dd'), // ✅ No permitir fechas pasadas
                        }}
                        fullWidth
                        size="small"
                        InputLabelProps={{ shrink: true }}
                        helperText="No se pueden seleccionar feriados nacionales"
                      />
                      <TextField
                        label="Nueva Hora Inicio"
                        type="time"
                        value={nuevaHoraInicio}
                        onChange={(e) => {
                          const [hours] = e.target.value.split(':');
                          const horaCompleta = `${hours}:00`;
                          setNuevaHoraInicio(horaCompleta);
                          
                          const nextHour = (parseInt(hours) + 1).toString().padStart(2, '0');
                          setNuevaHoraFin(`${nextHour}:00`);
                        }}
                        inputProps={{
                          step: 3600,
                          min: "08:00",
                          max: "19:00"
                        }}
                        fullWidth
                        size="small"
                        InputLabelProps={{ shrink: true }}
                        helperText="Solo horas completas"
                      />
                      <TextField
                        label="Nueva Hora Fin"
                        type="time"
                        value={nuevaHoraFin}
                        onChange={(e) => {
                          const [hours] = e.target.value.split(':');
                          setNuevaHoraFin(`${hours}:00`);
                        }}
                        inputProps={{
                          step: 3600,
                          min: "09:00",
                          max: "20:00"
                        }}
                        fullWidth
                        size="small"
                        InputLabelProps={{ shrink: true }}
                      />
                      <Stack direction="row" spacing={1}>
                        <Button
                          variant="contained"
                          color="primary"
                          onClick={onConfirmarReprogramacion}
                          fullWidth
                        >
                          Confirmar
                        </Button>
                        <Button
                          variant="outlined"
                          onClick={onCancelarReprogramacion}
                          fullWidth
                        >
                          Cancelar
                        </Button>
                      </Stack>
                    </Stack>
                  </Box>
                ) : programandoProximaSesion ? (
                  <Box>
                    <Typography variant="subtitle2" gutterBottom color="success.main">
                      Programar Próxima Sesión
                    </Typography>
                    <Stack spacing={2}>
                      <TextField
                        label="Fecha Próxima Sesión"
                        type="date"
                        value={proximaFecha}
                        onChange={(e) => setProximaFecha(e.target.value)}
                        fullWidth
                        size="small"
                        InputLabelProps={{ shrink: true }}
                      />
                      <TextField
                        label="Hora Inicio"
                        type="time"
                        value={proximaHoraInicio}
                        onChange={(e) => {
                          const [hours] = e.target.value.split(':');
                          const horaCompleta = `${hours}:00`;
                          setProximaHoraInicio(horaCompleta);
                          
                          const nextHour = (parseInt(hours) + 1).toString().padStart(2, '0');
                          setProximaHoraFin(`${nextHour}:00`);
                        }}
                        inputProps={{
                          step: 3600,
                          min: "08:00",
                          max: "19:00"
                        }}
                        fullWidth
                        size="small"
                        InputLabelProps={{ shrink: true }}
                      />
                      <TextField
                        label="Hora Fin"
                        type="time"
                        value={proximaHoraFin}
                        onChange={(e) => {
                          const [hours] = e.target.value.split(':');
                          setProximaHoraFin(`${hours}:00`);
                        }}
                        inputProps={{
                          step: 3600,
                          min: "09:00", 
                          max: "20:00"
                        }}
                        fullWidth
                        size="small"
                        InputLabelProps={{ shrink: true }}
                      />
                      <Stack direction="row" spacing={1}>
                        <Button
                          variant="contained"
                          color="success"
                          onClick={onConfirmarProximaSesion}
                          fullWidth
                        >
                          Programar
                        </Button>
                        <Button
                          variant="outlined"
                          onClick={onCancelarProximaSesion}
                          fullWidth
                        >
                          Cancelar
                        </Button>
                      </Stack>
                    </Stack>
                  </Box>
                ) : modificandoHorario ? (
                  <Box>
                    <Typography variant="subtitle2" gutterBottom color="warning.main">
                      Modificar Horario Recurrente
                    </Typography>
                    <Alert severity="info" sx={{ mb: 2, fontSize: '0.8rem' }}>
                      Se encontraron {citasAfectadas.filter(cita => {
                        const fechaCita = new Date(cita.fecha);
                        const fechaSeleccionada = new Date(selectedEvent.fecha);
                        return fechaCita.getDay() === fechaSeleccionada.getDay() &&
                              cita.hora_inicio === selectedEvent.horaInicio &&
                              fechaCita >= fechaSeleccionada;
                      }).length} citas futuras con este patrón para modificar.
                      <br />
                      <strong>Patrón actual:</strong> {(() => {
                        const dias = ['Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes', 'Sábado'];
                        const diaActual = new Date(selectedEvent.fecha).getDay();
                        return `${dias[diaActual]} de ${selectedEvent.horaInicio} a ${selectedEvent.horaFin}`;
                      })()}
                    </Alert>
                    <Stack spacing={2}>
                      <FormControl fullWidth size="small">
                        <InputLabel>Nuevo Día de la Semana</InputLabel>
                        <Select
                          value={nuevoPatronDia}
                          label="Nuevo Día de la Semana"
                          onChange={(e) => setNuevoPatronDia(e.target.value)}
                        >
                          <MenuItem value="lunes">Lunes</MenuItem>
                          <MenuItem value="martes">Martes</MenuItem>
                          <MenuItem value="miercoles">Miércoles</MenuItem>
                          <MenuItem value="jueves">Jueves</MenuItem>
                          <MenuItem value="viernes">Viernes</MenuItem>
                          <MenuItem value="sabado">Sábado</MenuItem>
                        </Select>
                      </FormControl>
                      <TextField
                        label="Nueva Hora Inicio"
                        type="time"
                        value={nuevoPatronHoraInicio}
                        onChange={(e) => {
                          const [hours] = e.target.value.split(':');
                          const horaCompleta = `${hours}:00`;
                          setNuevoPatronHoraInicio(horaCompleta);
                          
                          const nextHour = (parseInt(hours) + 1).toString().padStart(2, '0');
                          setNuevoPatronHoraFin(`${nextHour}:00`);
                        }}
                        inputProps={{
                          step: 3600,
                          min: "08:00",
                          max: "19:00"
                        }}
                        fullWidth
                        size="small"
                        InputLabelProps={{ shrink: true }}
                      />
                      <TextField
                        label="Nueva Hora Fin"
                        type="time"
                        value={nuevoPatronHoraFin}
                        onChange={(e) => {
                          const [hours] = e.target.value.split(':');
                          setNuevoPatronHoraFin(`${hours}:00`);
                        }}
                        inputProps={{
                          step: 3600,
                          min: "09:00",
                          max: "20:00"
                        }}
                        fullWidth
                        size="small"
                        InputLabelProps={{ shrink: true }}
                      />
                      <Stack direction="row" spacing={1}>
                        <Button
                          variant="contained"
                          color="warning"
                          onClick={onConfirmarModificarHorario}
                          fullWidth
                        >
                          Modificar Patrón
                        </Button>
                        <Button
                          variant="outlined"
                          onClick={onCancelarModificarHorario}
                          fullWidth
                        >
                          Cancelar
                        </Button>
                      </Stack>
                    </Stack>
                  </Box>
                // Modificaciones a la vista previa de impresión (líneas 607-670)
                ) : imprimiendoHorario ? (
                  <Box>
                    <Typography variant="subtitle2" gutterBottom color="primary">
                      Vista Previa del Horario a Imprimir
                    </Typography>
                    <Alert severity="info" sx={{ mb: 2, fontSize: '0.8rem' }}>
                      Se imprimirán {horarioParaImprimir.length} citas desde {format(new Date(selectedEvent.fecha), 'dd/MM/yyyy')} en adelante.
                    </Alert>
                    
                    {horarioParaImprimir.length > 0 && (
                      <Box sx={{ 
                        maxHeight: 300, 
                        overflow: 'auto', 
                        border: '1px solid #ddd', 
                        borderRadius: 1, 
                        p: 1, 
                        mb: 2,
                        // Simulación del ancho de la impresora térmica
                        width: '72mm', 
                        margin: '0 auto',
                        backgroundColor: '#fff' 
                      }}>
                        {/* Encabezado */}
                        <Typography 
                          variant="subtitle2" 
                          align="center" 
                          sx={{ 
                            fontWeight: 'bold', 
                            fontSize: '11px',
                            mb: 1 
                          }}
                        >
                          HORARIO DE CITAS
                        </Typography>
                        
                        <Typography 
                          variant="caption" 
                          sx={{ 
                            display: 'block',
                            textAlign: 'center',
                            fontWeight: 'medium',
                            fontSize: '9px',
                            mb: 1
                          }}
                        >
                          {horarioParaImprimir[0].paciente ? 
                            `${horarioParaImprimir[0].paciente.nombres} ${horarioParaImprimir[0].paciente.apellidos}` : 
                            'Sin paciente'}
                        </Typography>
                        
                        <Divider sx={{ mb: 1 }} />
                        
                        {/* Tabla de citas con formato optimizado para impresión térmica */}
                        <Box sx={{ fontSize: '8px' }}>
                          <Box sx={{ 
                            display: 'flex', 
                            justifyContent: 'space-between',
                            mb: 0.5,
                            fontWeight: 'bold'
                          }}>
                            <Typography variant="caption" sx={{ fontSize: '8px', width: '30%' }}>
                              FECHA
                            </Typography>
                            <Typography variant="caption" sx={{ fontSize: '8px', width: '40%' }}>
                              HORARIO
                            </Typography>
                            <Typography variant="caption" sx={{ fontSize: '8px', width: '30%' }}>
                              ESTADO
                            </Typography>
                          </Box>
                          
                          {horarioParaImprimir.map((cita, index) => (
                            <Box key={cita.id} sx={{ 
                              display: 'flex', 
                              justifyContent: 'space-between', 
                              py: 0.5, 
                              borderBottom: index < horarioParaImprimir.length - 1 ? '1px dotted #eee' : 'none' 
                            }}>
                              <Typography variant="caption" sx={{ fontSize: '8px', width: '30%' }}>
                                {format(new Date(cita.fecha), 'dd/MM/yy')}
                              </Typography>
                              <Typography variant="caption" sx={{ fontSize: '8px', width: '40%' }}>
                                {cita.hora_inicio} - {cita.hora_fin}
                              </Typography>
                              <Typography variant="caption" sx={{ fontSize: '8px', width: '30%' }}>
                                {cita.estado}
                              </Typography>
                            </Box>
                          ))}
                        </Box>
                        
                        <Divider sx={{ mt: 1, mb: 1 }} />
                        
                        <Typography 
                          variant="caption" 
                          align="center" 
                          sx={{ 
                            display: 'block',
                            fontSize: '7px',
                            fontStyle: 'italic'
                          }}
                        >
                          Por favor llegue 5 minutos antes de su cita
                        </Typography>
                      </Box>
                    )}
                    
                    <Stack direction="row" spacing={1}>
                      <Button
                        variant="contained"
                        color="primary"
                        onClick={onGenerarImpresion}
                        fullWidth
                        startIcon={<PrintIcon />}
                        disabled={horarioParaImprimir.length === 0}
                      >
                        Imprimir
                      </Button>
                      <Button
                        variant="outlined"
                        onClick={onCancelarImpresion}
                        fullWidth
                      >
                        Cancelar
                      </Button>
                    </Stack>
                  </Box>
                ) : (
                  <Stack spacing={1}>
                    <Button
                      variant="contained"
                      startIcon={<ScheduleIcon />}
                      onClick={() => onIniciarReprogramacion(selectedEvent)}
                      fullWidth
                      color="primary"
                    >
                      Reprogramar
                    </Button>
                    <Button
                      variant="outlined"
                      startIcon={<AddIcon />}
                      onClick={() => onIniciarProximaSesion(selectedEvent)}
                      fullWidth
                      color="success"
                    >
                      Próxima Sesión
                    </Button>
                    <Button
                      variant="outlined"
                      startIcon={<EditIcon />}
                      onClick={() => onIniciarModificarHorario(selectedEvent)}
                      fullWidth
                      color="warning"
                    >
                      Modificar Horario
                    </Button>
                    <Button
                      variant="outlined"
                      startIcon={<PrintIcon />}
                      onClick={() => onIniciarImprimirHorario(selectedEvent)}
                      fullWidth
                      color="info"
                    >
                      Imprimir Horario
                    </Button>

                    {/* ✅ NUEVA SECCIÓN: Opciones de Eliminación */}
                    <Divider sx={{ my: 1 }} />
                    <Typography variant="subtitle2" color="error" gutterBottom>
                      Opciones de Eliminación
                    </Typography>
                    <Stack spacing={1}>
                      <Button
                        variant="outlined"
                        color="error"
                        startIcon={<DeleteIcon />}
                        onClick={handleEliminarCita}
                        size="small"
                      >
                        Eliminar Permanentemente
                      </Button>
                      <Button
                        variant="outlined"
                        color="error"
                        startIcon={<DeleteSweepIcon />}
                        onClick={handleEliminarCitaYPosteriores}
                        size="small"
                      >
                        Eliminar Esta y Posteriores
                      </Button>
                    </Stack>
                  </Stack>
                )}
              </Paper>
            ) : (
              // VISTA PARA ROLES SIN PERMISOS (Especialista, Interno, etc.)
              <Paper elevation={1} sx={{ p: 2, backgroundColor: 'grey.50' }}>
                <Typography variant="subtitle2" gutterBottom>
                  📊 Información de la Cita
                </Typography>
                
                {/* Solo mostrar estado actual */}
                <Box mb={2}>
                  <Typography variant="caption" color="text.secondary">
                    Estado actual:
                  </Typography>
                  <Box mt={0.5}>
                    <Chip
                      label={selectedEvent.estado === 'Programada' ? 'Confirmada' : 
                             selectedEvent.estado === 'Cancelada' ? 'Justificada' : 
                             selectedEvent.estado}
                      sx={{ 
                        bgcolor: selectedEvent.color,
                        color: 'white',
                        fontWeight: 'medium'
                      }}
                    />
                  </Box>
                </Box>
                
                {/* Información adicional */}
                <Alert severity="info" variant="outlined">
                  <Typography variant="caption">
                    Para modificar esta cita, contacte al área de Admisión.
                  </Typography>
                </Alert>
                
                {/* Botón de ver detalles (solo lectura) */}
                <Box mt={2}>
                  <Button
                    variant="outlined"
                    startIcon={<VisibilityIcon />}
                    onClick={onViewCita}
                    fullWidth
                    color="primary"
                  >
                    Ver Detalles Completos
                  </Button>
                </Box>
              </Paper>
            )}
          </Grid>
        </Grid>
      </DialogContent>
    </Dialog>
  );
};

export default EventDialog;